import React from "react";
import { BrowserRouter as Router, Route, Routes, Navigate, useLocation } from "react-router-dom";
import Header from "./components/Header.js";
import Login from "./pages/Login.js";
import Teachers from "./components/Teachers.js";
import Students from "./components/Students.js";
import Billing from "./components/Billing.js";
import { ServiceProvider, useServices } from "./services/ServiceContext";
import SuUsers from "./components/SuUsers.js";
import { PaymentProvider } from "./services/PaymentService.js";
import TeacherLessons from "./components/TeacherLessons.js";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import { ScheduleProvider } from "./services/ScheduleService.js";
import StudentLessons from "./components/StudentLessons.js";
import StudentSchedule from "./components/StudentSchedule.js";
import TeacherSchedule from "./components/TeacherSchedule.js";
import ChangePassword from "./components/ChangePassword.js";
import EditProfile from "./components/EditProfile.js";
import Settings from "./components/Settings.js";
import ActivationPage from "./components/ActivationPage.js";
import { ProtectedRoute } from "./components/ProtectedRoute.js";
import { useSelector } from "react-redux";
import ForbiddenPage from "./components/ForbiddenPage.js";
import NotFoundPage from "./components/NotFoundPage.js";
import { ErrorProvider } from "./services/ErrorContext.js";
import GlobalErrorModal from "./services/modals/GlobalErrorModal.js";
import About from "./components/About.js";
import ResetPage from "./components/ResetPage.js";
import StudentSummary from "./components/StudentSummary.js";
import GlobalBackdrop from "./infrastructure/backdrop/GlobalBackdrop.js";
import TeacherSummary from "./components/TeacherSummary.js";
import TeacherBotTelegram from "./components/TeacherBotTelegram.js";
import InstagramCampaigns from "./components/InstagramCampaigns.js";
import InstagramCampaignDetail from "./components/InstagramCampaignDetail.js";
import OutwriteTasks from "./components/OutwriteTasks.js";
import OutwriteLlm from "./components/OutwriteLlm.js";
import OutwriteSetup from "./components/OutwriteSetup.js";
import OutwriteLogs from "./components/OutwriteLogs.js";
import OutwriteTests from "./components/OutwriteTests.js";
import OutwriteLearnerHome from "./components/OutwriteLearnerHome.js";
import OutwriteLearnerPractice from "./components/OutwriteLearnerPractice.js";
import TeacherLandingShell from "./landing/TeacherLandingShell.js";
import TeacherLandingHome from "./landing/TeacherLandingHome.js";
import TeacherLandingAbout from "./landing/TeacherLandingAbout.js";
import TeacherLandingCourses from "./landing/TeacherLandingCourses.js";
import TeacherLandingOffer from "./landing/TeacherLandingOffer.js";

function AppContent() {
  const theme = createTheme({
    components: {
      MuiOutlinedInput: {
        styleOverrides: {
          root: ({ ownerState }) => ({
            ...(ownerState.readOnly && {
              backgroundColor: "#f0f0f0",
              "& fieldset": {
                borderColor: "#9e9e9e",
              },
            }),
          }),
        },
      },
    },
  });

  const { lesson } = useServices();
  const user = useSelector((state) => state.auth.user);
  const location = useLocation();
  const isTeacherLanding = /^\/users\/teacher\/[^/]+\/landing(\/|$)/.test(location.pathname);

  const redirectToMap = {
    'student': '/student/summary',
    'teacher': '/teacher/summary',
    'admin': '/users',
    'superuser': '/users',
    'outwrite': '/outwrite/tasks',
    'outwrite_learner': '/outwrite/learn',
    'undefined': '/login',
  }

  return (
    <ThemeProvider theme={theme}>
      {!isTeacherLanding && <Header className="header" />}
      <div className={isTeacherLanding ? "app main-content tl-host" : "app main-content"}>
        <Routes>
          <Route path="/users/teacher/:teacherId/landing" element={<TeacherLandingShell />}>
            <Route index element={<TeacherLandingHome />} />
            <Route path="about" element={<TeacherLandingAbout />} />
            <Route path="courses" element={<TeacherLandingCourses />} />
            <Route path="offer" element={<TeacherLandingOffer />} />
          </Route>

          <Route path="/login" element={<Login />} />

          <Route element={<ProtectedRoute groupNo='1' role={user} isAllowed={true} redirectTo={redirectToMap[user?.type]}/>}>
            <Route path="/" element={
              user
               ? <Navigate to={redirectToMap[user.type]} replace />
               : <Navigate to="/login" replace />
              }
            />
            <Route path="/activate" element={<ActivationPage />} />
          </Route>

          <Route element={<ProtectedRoute groupNo='2' role={user} isAllowed={user} />}>
            <Route path="/settings" element={<Settings user={user}/>} />
            <Route path="/edit-profile" element={<EditProfile />} />
            <Route path="/about" element={<About />} />
            <Route path="/change-password" element={<ChangePassword />} />
          </Route>

          <Route element={<ProtectedRoute groupNo='3' role={user} isAllowed={user?.type === 'superuser'} />}>
            <Route path="/users" element={<SuUsers />} />
          </Route>

          <Route element={<ProtectedRoute groupNo='4' role={user} isAllowed={user?.type === 'admin'} />}>
            {/* <Route path="/register" element={<Register />} /> */}
          </Route>

          <Route element={<ProtectedRoute groupNo='5' role={user} isAllowed={user?.type === 'teacher'} />}>
            <Route path="/teacher/schedule" element={<TeacherSchedule />} />
            <Route path="/students" element={<Students />} />
            <Route path="/billing" element={<PaymentProvider><Billing /></PaymentProvider>} />
            <Route path="/teacher/lessons" element={<ScheduleProvider><TeacherLessons /></ScheduleProvider>} />
            <Route path="/teacher/summary" element={
              <PaymentProvider>
                  <TeacherSummary user={user} />
              </PaymentProvider>} />
            <Route path="/teacher/bot/telegram" element={<TeacherBotTelegram />} />
            <Route path="/teacher/bot/instagram/campaigns" element={<InstagramCampaigns />} />
            <Route path="/teacher/bot/instagram/campaigns/:campaignId" element={<InstagramCampaignDetail />} />
            </Route>

          <Route element={<ProtectedRoute groupNo='6' role={user} isAllowed={user?.type === 'student'} />}>
            <Route path="/student/schedule" element={<StudentSchedule />} />
            <Route path="/teachers" element={<Teachers />} />
            <Route path="/student/lessons" element={<ScheduleProvider><StudentLessons /></ScheduleProvider>} />
            <Route path="/student/summary" element={<StudentSummary user={user} />} />
          </Route>

          <Route element={<ProtectedRoute groupNo='7' role={user} isAllowed={user?.type === 'outwrite'} />}>
            <Route path="/outwrite/tasks" element={<OutwriteTasks />} />
            <Route path="/outwrite/tests" element={<OutwriteTests />} />
            <Route path="/outwrite/llm" element={<OutwriteLlm />} />
            <Route path="/outwrite/setup" element={<OutwriteSetup />} />
            <Route path="/outwrite/logs" element={<OutwriteLogs />} />
          </Route>

          <Route element={<ProtectedRoute groupNo='8' role={user} isAllowed={user?.type === 'outwrite_learner'} />}>
            <Route path="/outwrite/learn" element={<OutwriteLearnerHome />} />
            <Route path="/outwrite/learn/:givenId" element={<OutwriteLearnerPractice />} />
          </Route>

          {/* <Route path="/login" element={<Login />} /> */}
          <Route path="/reset-password" element={<ResetPage />} />
          <Route path="/403" element={<ForbiddenPage />} />
          <Route path="/*" element={<NotFoundPage />} />

        </Routes>
      </div>
      {/* <Footer /> */}

      <lesson.LessonModal />
    </ThemeProvider>
  );
}

function App() {
  return (
    <ErrorProvider>
      <GlobalErrorModal />
      <GlobalBackdrop />
      <ServiceProvider>
        <Router>
          <AppContent />
        </Router>
      </ServiceProvider>
    </ErrorProvider>
  );
}

export default App;